import { useNavigate } from 'react-router-dom';

interface Author {
    firstName: string;
    lastName: string;
}

interface Dossier {
    _id: string;
    title: string;
    description: string;
    slug: string;
    author: Author;
    thumbnailUrl?: string;
}

interface EditorChoiceGridProps {
    dossiers: Dossier[];
    loading: boolean;
}

const EditorChoiceGrid: React.FC<EditorChoiceGridProps> = ({ dossiers, loading }) => {
    const navigate = useNavigate();

    const handleDossierClick = (dossier: Dossier) => {
        navigate(`/dossiers/${dossier._id}`);
    };

    // Loading skeleton
    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="animate-pulse">
                        <div className="bg-gray-200 dark:bg-gray-700 h-48 rounded-t-lg"></div>
                        <div className="bg-white dark:bg-mcdm-third-400 p-4 rounded-b-lg space-y-3">
                            <div className="bg-gray-200 dark:bg-gray-700 h-6 rounded w-3/4"></div>
                            <div className="bg-gray-200 dark:bg-gray-700 h-4 rounded w-full"></div>
                            <div className="bg-gray-200 dark:bg-gray-700 h-4 rounded w-5/6"></div>
                            <div className="bg-gray-200 dark:bg-gray-700 h-3 rounded w-1/2"></div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (dossiers.length === 0) return null;

    // Grid with dossiers
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dossiers.map((dossier) => (
                <div
                    key={dossier._id}
                    onClick={() => handleDossierClick(dossier)}
                    className="bg-white dark:bg-mcdm-third-400 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer group"
                >
                    {/* Thumbnail */}
                    <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 overflow-hidden">
                        {dossier.thumbnailUrl ? (
                            <img
                                src={dossier.thumbnailUrl}
                                alt={dossier.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-mcdm-secondary-300 to-mcdm-secondary-400">
                                <div className="text-white text-center p-4">
                                    <svg
                                        className="w-12 h-12 mx-auto mb-2 opacity-75"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                    <p className="text-sm font-medium">Dossier</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Content */}
                    <div className="p-4">
                        <h3 className="font-platypi text-lg text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2 line-clamp-2 group-hover:text-mcdm-secondary-500 dark:group-hover:text-mcdm-secondary-300 transition-colors">
                            {dossier.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
                            {dossier.description}
                        </p>
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                            <svg
                                className="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                            </svg>
                            <span>
                                {dossier.author.firstName} {dossier.author.lastName}
                            </span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default EditorChoiceGrid;
